import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Droppable } from '@hello-pangea/dnd';
import { Inbox, Search, Filter, Plus, Trash2, X, CheckSquare } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select } from '../ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { DraggableTaskCard, createRenderTaskClone } from './DraggableTaskCard';
import { DraggableTicketCard, createRenderTicketClone } from './DraggableTicketCard';
import {
  Task,
  TaskArea,
  TaskPriority,
  TaskAreaLabels,
  TaskPriorityLabels
} from '../../types/task';
import {
  Ticket,
  TicketPriority as TicketPriorityType,
  TicketArea as TicketAreaType,
  ticketPriorityLabels,
  ticketAreaLabels
} from '../../types/ticket';

type ActiveTab = 'tarefas' | 'chamados';

interface UnassignedTasksPoolProps {
  tasks: Task[];
  unassignedTickets: Ticket[];
  onEditTask: (task: Task) => void;
  onDuplicateTask: (task: Task) => void;
  onCreateTask: () => void;
  onCreateTicket?: () => void;
  onDeleteTask?: (task: Task) => void;
  onBulkDeleteTasks?: (taskIds: string[]) => void;
  onEditTicket?: (ticket: Ticket) => void;
  onDeleteTicket?: (ticket: Ticket) => void;
  projectId?: string;
  serviceOrderId?: string;
}

export const UnassignedTasksPool: React.FC<UnassignedTasksPoolProps> = ({
  tasks,
  unassignedTickets,
  onEditTask,
  onDuplicateTask,
  onCreateTask,
  onCreateTicket,
  onDeleteTask,
  onBulkDeleteTasks,
  onEditTicket,
  onDeleteTicket,
  projectId,
  serviceOrderId
}) => {
  const [activeTab, setActiveTab] = useState<ActiveTab>('tarefas');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterArea, setFilterArea] = useState<TaskArea | ''>('');
  const [filterPriority, setFilterPriority] = useState<TaskPriority | ''>('');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest'>('newest');

  // Ticket filter state
  const [ticketSearchTerm, setTicketSearchTerm] = useState('');
  const [ticketFilterArea, setTicketFilterArea] = useState<TicketAreaType | ''>('');
  const [ticketFilterPriority, setTicketFilterPriority] = useState<TicketPriorityType | ''>('');

  // Filter tickets
  const filteredTickets = unassignedTickets
    .filter(t => {
      const matchesSearch = t.titulo.toLowerCase().includes(ticketSearchTerm.toLowerCase()) ||
                            t.descricao?.toLowerCase().includes(ticketSearchTerm.toLowerCase());
      const matchesArea = !ticketFilterArea || t.area === ticketFilterArea;
      const matchesPriority = !ticketFilterPriority || t.prioridade === ticketFilterPriority;
      return matchesSearch && matchesArea && matchesPriority;
    })
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  const hasTicketFilters = ticketSearchTerm || ticketFilterArea || ticketFilterPriority;

  const clearTicketFilters = () => {
    setTicketSearchTerm('');
    setTicketFilterArea('');
    setTicketFilterPriority('');
  };

  // Selection state
  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<string>>(new Set());

  // Clear selection when filters change or tasks change
  useEffect(() => {
    setSelectedTaskIds(new Set());
  }, [searchTerm, filterArea, filterPriority, sortBy]);

  // Remove deleted tasks from selection
  useEffect(() => {
    setSelectedTaskIds(prev => {
      const taskIdSet = new Set(tasks.map(t => t._id));
      const newSelection = new Set<string>();
      prev.forEach(id => {
        if (taskIdSet.has(id)) {
          newSelection.add(id);
        }
      });
      return newSelection.size !== prev.size ? newSelection : prev;
    });
  }, [tasks]);

  // Filter and sort tasks
  const filteredTasks = tasks
    .filter(task => {
      const matchesSearch = task.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           task.description?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesArea = !filterArea || task.area === filterArea;
      const matchesPriority = !filterPriority || task.prioridade === filterPriority;

      return matchesSearch && matchesArea && matchesPriority;
    })
    .sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return sortBy === 'oldest' ? dateA - dateB : dateB - dateA;
    });

  const clearFilters = () => {
    setSearchTerm('');
    setFilterArea('');
    setFilterPriority('');
    setSortBy('newest');
  };

  const hasActiveFilters = searchTerm || filterArea || filterPriority;

  // Selection helpers
  const toggleTaskSelection = (taskId: string) => {
    setSelectedTaskIds(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  };

  const selectAllTasks = () => {
    setSelectedTaskIds(new Set(filteredTasks.map(t => t._id)));
  };

  const clearSelection = () => {
    setSelectedTaskIds(new Set());
  };

  const handleBulkDelete = () => {
    if (selectedTaskIds.size === 0 || !onBulkDeleteTasks) return;

    const confirmed = window.confirm(
      `Tem certeza que deseja excluir ${selectedTaskIds.size} tarefa(s)? Esta ação não pode ser desfeita.`
    );

    if (confirmed) {
      onBulkDeleteTasks(Array.from(selectedTaskIds));
      setSelectedTaskIds(new Set());
    }
  };

  const showSelection = !!(onDeleteTask || onBulkDeleteTasks);

  return (
    <Card variant="glass" className="h-full flex flex-col relative">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Inbox className="w-5 h-5 text-blue-400" />
            <CardTitle className="text-lg text-foreground-primary">
              {activeTab === 'tarefas' ? 'Tarefas Não Atribuídas' : 'Chamados Sem OS'}
            </CardTitle>
            <span className="text-sm text-foreground-tertiary">
              {activeTab === 'tarefas'
                ? `(${filteredTasks.length}/${tasks.length})`
                : `(${filteredTickets.length}/${unassignedTickets.length})`
              }
            </span>
          </div>

          <Button
            variant="gradient"
            size="sm"
            onClick={activeTab === 'tarefas' ? onCreateTask : (onCreateTicket || onCreateTask)}
            className="shrink-0"
          >
            <Plus className="w-4 h-4 mr-1" />
            {activeTab === 'tarefas' ? 'Nova Tarefa' : 'Novo Chamado'}
          </Button>
        </div>

        {/* Pill Selector */}
        {projectId && (
          <div className="flex bg-background-secondary rounded-lg p-0.5 mt-2">
            <button
              onClick={() => setActiveTab('tarefas')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'tarefas' ? 'bg-blue-500 text-white' : 'text-foreground-secondary hover:text-foreground-primary'
              }`}
            >
              Tarefas
            </button>
            <button
              onClick={() => setActiveTab('chamados')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'chamados' ? 'bg-blue-500 text-white' : 'text-foreground-secondary hover:text-foreground-primary'
              }`}
            >
              Chamados
            </button>
          </div>
        )}

        {/* Search */}
        <div className="relative pt-2 pb-2">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-foreground-tertiary" />
          {activeTab === 'tarefas' ? (
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar tarefas..."
              className="pl-10"
            />
          ) : (
            <Input
              value={ticketSearchTerm}
              onChange={(e) => setTicketSearchTerm(e.target.value)}
              placeholder="Buscar chamados..."
              className="pl-10"
            />
          )}
        </div>

        {/* Filters */}
        {activeTab === 'tarefas' ? (
          <div className="flex gap-2 flex-wrap">
            <div className="flex items-center gap-1">
              <Filter className="w-3 h-3 text-foreground-tertiary" />
            </div>

            <Select
              value={filterArea}
              onChange={(value) => setFilterArea(value as TaskArea | '')}
              options={[
                { value: '', label: 'Todas as áreas' },
                ...Object.entries(TaskAreaLabels).map(([value, label]) => ({ value, label }))
              ]}
              className="text-xs min-w-[100px]"
            />

            <Select
              value={filterPriority}
              onChange={(value) => setFilterPriority(value as TaskPriority | '')}
              options={[
                { value: '', label: 'Todas prioridades' },
                ...Object.entries(TaskPriorityLabels).map(([value, label]) => ({ value, label }))
              ]}
              className="text-xs min-w-[120px]"
            />

            <Select
              value={sortBy}
              onChange={(value) => setSortBy(value as 'newest' | 'oldest')}
              options={[
                { value: 'newest', label: 'Recentes primeiro' },
                { value: 'oldest', label: 'Antigas primeiro' }
              ]}
              className="text-xs min-w-[150px]"
            />

            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-xs h-7 px-2 text-foreground-tertiary hover:text-foreground-primary"
              >
                Limpar
              </Button>
            )}
          </div>
        ) : (
          <div className="flex gap-2 flex-wrap">
            <div className="flex items-center gap-1">
              <Filter className="w-3 h-3 text-foreground-tertiary" />
            </div>

            <Select
              value={ticketFilterArea}
              onChange={(value) => setTicketFilterArea(value as TicketAreaType | '')}
              options={[
                { value: '', label: 'Todas as áreas' },
                ...Object.entries(ticketAreaLabels).map(([value, label]) => ({ value, label }))
              ]}
              className="text-xs min-w-[100px]"
            />

            <Select
              value={ticketFilterPriority}
              onChange={(value) => setTicketFilterPriority(value as TicketPriorityType | '')}
              options={[
                { value: '', label: 'Todas prioridades' },
                ...Object.entries(ticketPriorityLabels).map(([value, label]) => ({ value, label }))
              ]}
              className="text-xs min-w-[120px]"
            />

            {hasTicketFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearTicketFilters}
                className="text-xs h-7 px-2 text-foreground-tertiary hover:text-foreground-primary"
              >
                Limpar
              </Button>
            )}
          </div>
        )}
      </CardHeader>

      <CardContent className="flex-1 pt-0 overflow-hidden">
        {activeTab === 'tarefas' ? (
          <Droppable
            droppableId="unassigned-tasks"
            type="task"
            renderClone={createRenderTaskClone(filteredTasks, onEditTask, onDuplicateTask, onDeleteTask)}
          >
            {(provided, snapshot) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className={`
                  h-full w-full overflow-y-auto rounded-lg border-2 border-dashed p-3 transition-colors
                  ${snapshot.isDraggingOver
                    ? 'border-green-500/50 bg-green-500/10'
                    : 'border-white/10 bg-white/5'
                  }
                `}
              >
                {filteredTasks.length > 0 ? (
                  filteredTasks.map((task, index) => (
                    <DraggableTaskCard
                      key={task._id}
                      task={task}
                      index={index}
                      onEdit={onEditTask}
                      onDuplicate={onDuplicateTask}
                      onDelete={onDeleteTask}
                      showSelection={showSelection}
                      isSelected={selectedTaskIds.has(task._id)}
                      onSelect={toggleTaskSelection}
                    />
                  ))
                ) : (
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center text-foreground-tertiary">
                      <Inbox className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      {hasActiveFilters ? (
                        <>
                          <p className="mb-2">Nenhuma tarefa encontrada</p>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={clearFilters}
                            className="text-blue-400 hover:text-blue-300"
                          >
                            Limpar filtros
                          </Button>
                        </>
                      ) : tasks.length === 0 ? (
                        <>
                          <p className="mb-3">Nenhuma tarefa não atribuída</p>
                          <Button
                            variant="gradient"
                            size="sm"
                            onClick={onCreateTask}
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Criar primeira tarefa
                          </Button>
                        </>
                      ) : (
                        <p>Todas as tarefas estão atribuídas</p>
                      )}
                    </div>
                  </div>
                )}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        ) : (
          /* Tickets tab content */
          <Droppable
            droppableId="unassigned-tickets"
            type="task"
            renderClone={createRenderTicketClone(filteredTickets)}
          >
            {(provided, snapshot) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className={`
                  h-full w-full overflow-y-auto rounded-lg border-2 border-dashed p-3 transition-colors
                  ${snapshot.isDraggingOver
                    ? 'border-green-500/50 bg-green-500/10'
                    : 'border-white/10 bg-white/5'
                  }
                `}
              >
                {filteredTickets.length > 0 ? (
                  filteredTickets.map((ticket, index) => (
                    <DraggableTicketCard
                      key={ticket._id}
                      ticket={ticket}
                      index={index}
                      onEdit={onEditTicket}
                      onDelete={onDeleteTicket}
                    />
                  ))
                ) : (
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center text-foreground-tertiary">
                      <Inbox className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      {hasTicketFilters ? (
                        <>
                          <p className="mb-2">Nenhum chamado encontrado</p>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={clearTicketFilters}
                            className="text-blue-400 hover:text-blue-300"
                          >
                            Limpar filtros
                          </Button>
                        </>
                      ) : (
                        <p>Nenhum chamado sem OS</p>
                      )}
                    </div>
                  </div>
                )}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        )}
      </CardContent>

      {/* Floating Action Bar for bulk operations - Rendered via Portal */}
      {selectedTaskIds.size > 0 && onBulkDeleteTasks && createPortal(
        <div
          style={{
            position: 'fixed',
            bottom: '24px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 99999,
          }}
        >
          <div className="bg-gray-900/95 backdrop-blur-md shadow-2xl border border-blue-500/30 rounded-lg p-3 px-6">
            <div className="flex items-center gap-6">
              <span className="text-sm text-foreground-secondary whitespace-nowrap">
                {selectedTaskIds.size} tarefa{selectedTaskIds.size > 1 ? 's' : ''} selecionada{selectedTaskIds.size > 1 ? 's' : ''}
              </span>

              <div className="h-4 w-px bg-white/20" />

              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={selectAllTasks}
                  className="text-foreground-secondary hover:text-foreground-primary"
                >
                  <CheckSquare className="w-4 h-4 mr-1" />
                  Selecionar todas
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearSelection}
                  className="text-foreground-tertiary hover:text-foreground-primary"
                >
                  <X className="w-4 h-4 mr-1" />
                  Limpar
                </Button>

                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleBulkDelete}
                  className="bg-red-500 hover:bg-red-600 text-white"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Excluir selecionadas
                </Button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </Card>
  );
};